import Tokenizr from "tokenizr";
import { hasNoElements, lastOrDefault } from "./Array";
import { isNotNullOrWhiteSpace, isNullOrWhiteSpace, unescapeAllQuoteTypes } from "./String";

export type TokenSet = [string, string];

export function toTokenSets(expression: string): TokenSet[] {
  if (isNullOrWhiteSpace(expression)) {
    return [];
  }

  const lex = getLexer();
  lex.input(expression);

  let foundSeparator = false;
  const tokenSets = [];
  let token = null;

  do {
    token = lex.token();

    if (token == null || token.type === `EOF`) {
      break;
    }

    const { type, value } = token;
    const isStarSearch = !foundSeparator && type === `WRD` && isNotNullOrWhiteSpace(value);
    const isFieldSearch = foundSeparator && type === `WRD` && isNotNullOrWhiteSpace(value);

    if (isStarSearch) {
      tokenSets.push([`*`, value]);
    } else if (isFieldSearch) {
      const [, fieldName] = tokenSets.pop();
      tokenSets.push([fieldName, value]);
      foundSeparator = false;
    } else if (type === `SEP`) {
      foundSeparator = true;
    } else {
      foundSeparator = false;
    }
  } while (token != null);

  return tokenSets;
}

export function toExpression(tokenSets: TokenSet[]): string {
  if (hasNoElements(tokenSets)) {
    return ``;
  }

  return tokenSets
    .reduce((output, token) => {
      if (hasNoElements(token)) {
        return output;
      }

      const [fieldName, search] = token;
      if (fieldName.includes(`*`)) {
        output.push(sanitizeSearch(search));
        return output;
      }

      output.push(`${fieldName}:${sanitizeSearch(search)}`);
      return output;
    }, [])
    .join(` `);
}

export function sanitizeSearch(search: string): string {
  if (isNullOrWhiteSpace(search)) {
    return search;
  }

  if (/[\s:]/i.test(search)) {
    return `"${search}"`;
  }

  return search;
}

export function indexOfTokenSet(tokenSets: TokenSet[], fieldName: string, search: string): number {
  return tokenSets.findIndex(([f, s]) => f === fieldName && s === search);
}

export function mergeTokenSets(currentTokenSets: TokenSet[], newTokenSets: TokenSet[]): TokenSet[] {
  let output = [...currentTokenSets];

  for (const [fieldName, search] of newTokenSets) {
    if (fieldName === `*`) {
      output = mergeStarTokenSet(output, search);
      continue;
    }

    output = mergeFieldTokenSet(output, fieldName, search);
  }

  return output;
}

export function hasInjectedToken(expression: string): boolean {
  return getInjectedTokenRegExp().test(expression);
}

export function extractInjectedToken(expression: string): string {
  const matches = Array.from(expression.matchAll(getInjectedTokenRegExp()), ([, fieldName]) => `${fieldName}:`);
  return matches[matches.length - 1];
}

const INJECTED_TOKEN_REGEX = /{{(.+?)}}/gi;
function getInjectedTokenRegExp(): RegExp {
  INJECTED_TOKEN_REGEX.lastIndex = -1;
  return INJECTED_TOKEN_REGEX;
}

let lexer: Tokenizr = null;
function getLexer(): Tokenizr {
  if (lexer != null) {
    return lexer;
  }

  lexer = new Tokenizr();

  // ignore all whitespace
  lexer.rule(/\s/i, (ctx) => {
    ctx.ignore();
  });

  // ignore star used in multi-field-searches
  lexer.rule(/\*/i, (ctx) => {
    ctx.ignore();
  });

  // seperator between field names and values
  lexer.rule(/:/i, (ctx) => {
    ctx.accept(`SEP`);
  });

  // ignore injected tokens e.g. {{city}}
  lexer.rule(/{{.*?}}/i, (ctx) => {
    ctx.ignore();
  });

  lexer.rule(/"((?:\\"|\\'|[^\r\n])*)"/, (ctx, match) => {
    ctx.accept(`WRD`, unescapeAllQuoteTypes(match[1]));
  });

  // simple token eg. Hamburg or field names like city
  lexer.rule(/([^:"'\s]+)/i, (ctx, [, value]) => {
    ctx.accept(`WRD`, value);
  });

  // token enclosed in quotes e.g. "Frankfurt am Main" or '<=2023-02-01T10:15:00.646Z'
  // test: https://regex101.com/r/HG3y8o/2
  lexer.rule(/['"](.*?)['"]/i, (ctx, [, value]) => {
    ctx.accept(`WRD`, value);
  });

  // if nothing matches and token begins with quotes take everything until the end
  // test: https://regex101.com/r/msRStp/2
  lexer.rule(/(['"].*)$/i, (ctx, [, value]) => {
    ctx.accept(`WRD`, value);
  });

  return lexer;
}

function indexOfFieldSearch(tokenSets: TokenSet[], fieldName: string) {
  return tokenSets.findIndex(([f]) => f === fieldName);
}

function mergeStarTokenSet(currentTokenSets: TokenSet[], search: string): TokenSet[] {
  const index = indexOfTokenSet(currentTokenSets, `*`, search);
  if (index >= 0) {
    return currentTokenSets;
  }

  currentTokenSets.push([`*`, search]);
  return currentTokenSets;
}

function mergeFieldTokenSet(currentTokenSets: TokenSet[], fieldName: string, search: string): TokenSet[] {
  const output = currentTokenSets;
  const index = indexOfFieldSearch(currentTokenSets, fieldName);
  if (index < 0) {
    currentTokenSets.push([fieldName, search]);
    return currentTokenSets;
  }

  output[index] = [fieldName, search];
  return currentTokenSets;
}

export function getLastSearchTerm(search: string): TokenSet {
  if (isNullOrWhiteSpace(search)) {
    return [null, ``];
  }

  return lastOrDefault(toTokenSets(search)) ?? [null, ``];
}

export function getFilteredSearchTerms(search: string, withoutTokenSetList: TokenSet[]): TokenSet[] {
  if (isNullOrWhiteSpace(search)) {
    return null;
  }

  const tokenSetList = toTokenSets(search);

  if (hasNoElements(withoutTokenSetList)) {
    return tokenSetList;
  }

  return tokenSetList.filter(
    (tokenSet) => !withoutTokenSetList.some(
      (withoutTokenSet) => tokenSet[0] === withoutTokenSet[0] && tokenSet[1] === withoutTokenSet[1]
    )
  );
}
